<script setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import Provider from '@/api/Provider.js'
import WorldMap from '@/components/Provider/WorldMap.vue'
import SubmitProviderData from '@/components/SlideOver/forms/SubmitProviderData.vue'
import { useMainStore } from '@/stores/main'

const route = useRoute()
const mainStore = useMainStore()
const provider = ref(null)
const loading = ref(true)
const error = ref(null)
const mapData = ref({})
const isExporting = ref(false)

onMounted(async () => {
  try {
    const providerName = route.params.name
    const result = await Provider.getProvider(providerName)

    if (result.status === 'not_found') {
      error.value = 'Provider not found'
    } else if (result.status && result.status !== 'success') {
      error.value = 'Error loading provider'
    } else {
      provider.value = result

      // Load geographic data
      try {
        const geoData = await Provider.getProviderGeography(providerName)
        mapData.value = geoData
      } catch (geoErr) {
        console.warn('Could not load geographic data:', geoErr)
        // Geographic data is optional, don't fail the whole page
      }
    }
  } catch (err) {
    error.value = 'Error loading provider'
    console.error(err)
  } finally {
    loading.value = false
  }
})

const formatNumber = (num) => {
  if (!num) return '0'
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toString()
}

const calculateChangePercentage = (current, previous) => {
  if (!previous || previous === 0) return 0
  return ((current - previous) / previous * 100).toFixed(1)
}

const handleImageError = (event) => {
  // Hide the failed image and show fallback icon
  event.target.style.display = 'none'
  // Clear thumbnail_url to trigger fallback display
  if (provider.value) {
    provider.value.thumbnail_url = null
  }
}

const exportData = async () => {
  if (!provider.value || isExporting.value) return

  isExporting.value = true

  try {
    const result = await Provider.exportProviderIPs(provider.value.name)

    if (result.status === 'success') {
      console.log('Export started successfully')
    } else if (result.status === 'unauthorized') {
      error.value = 'You need to be logged in to export data'
    } else if (result.status === 'ratelimited') {
      error.value = 'Too many requests. Please try again later.'
    } else {
      error.value = 'Failed to export data. Please try again.'
    }
  } catch (err) {
    console.error('Export error:', err)
    error.value = 'An unexpected error occurred during export'
  } finally {
    isExporting.value = false
  }
}

const openSubmitForm = () => {
  mainStore.show_slideover(
    SubmitProviderData,
    {
      providerName: provider.value.name,
      providerDisplayName: provider.value.display_name || provider.value.name,
      providerThumbnail: provider.value.thumbnail_url || null
    },
    { width: 40 },
    'Submit Provider Data'
  )
}
</script>

<template>
  <div class="container-fluid provider-dashboard">
    <!-- Loading State -->
    <div v-if="loading" class="container provider-content">
      <div class="loading-state">
        <div class="spinner"></div>
        <p>Loading provider details...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="container provider-content">
      <div class="error-state">
        <h2>{{ error }}</h2>
        <router-link to="/providers" class="back-btn">
          ← Back to Providers
        </router-link>
      </div>
    </div>

    <!-- Main Dashboard -->
    <div v-else-if="provider">
      <!-- World Map Section - Outside container for full width -->
      <div class="map-section">
        <div class="map-container">
          <WorldMap :data="mapData" :height="1000" />
        </div>
      </div>

      <!-- Content Container -->
      <div class="container provider-content">
        <!-- Content Section with floating infrastructure overview -->
      <div class="content-section">
        <!-- Infrastructure Overview - floated to the right -->
        <div class="infrastructure-overview">
          <div class="provider-overview">
            <div class="overview-header">
              <h3>{{ provider.display_name || provider.name }} Infrastructure</h3>
              <div class="overview-amount">{{ formatNumber(provider.stats?.total || 0) }} IPs</div>
            </div>
            <div class="overview-details">
              <div class="overview-item">
                <span class="overview-label">Weekly growth</span>
                <span class="overview-value positive">{{ provider.stats?.delta_week || 0 }} IPs</span>
              </div>
              <div class="overview-item">
                <span class="overview-label">Daily changes</span>
                <span class="overview-value">{{ formatNumber(provider.stats?.delta_day || 0) }} IPs</span>
              </div>
            </div>
            <div class="action-buttons">
              <button class="action-btn primary" @click="openSubmitForm">Add IPs</button>
              <button
                class="action-btn secondary"
                @click="exportData"
                :disabled="isExporting"
              >
                {{ isExporting ? 'Exporting...' : 'Export Data' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Provider Description Section - flows around the infrastructure overview -->
        <div class="description-section">
          <div class="about-header">
            <div class="provider-icon">
              <!-- Show thumbnail if available -->
              <img
                v-if="provider.thumbnail_url"
                :src="provider.thumbnail_url"
                :alt="provider.display_name || provider.name"
                class="provider-thumbnail"
                @error="handleImageError"
              />
              <!-- Fallback to FontAwesome icons -->
              <template v-else>
                <font-awesome-icon v-if="provider.name === 'Digital Ocean'" style="color: #0069ff; font-size: 1.2rem;" :icon="['fab', 'digital-ocean']" />
                <font-awesome-icon v-else-if="provider.name === 'Azure'" style="color: #0069ff; font-size: 1.2rem;" :icon="['fab', 'windows']" />
                <font-awesome-icon v-else-if="provider.name === 'Amazon'" style="color: #ff9900; font-size: 1.2rem;" :icon="['fab', 'aws']" />
                <font-awesome-icon v-else-if="provider.name === 'Cloudflare'" style="color: #f4801e; font-size: 1.2rem;" :icon="['fab', 'cloudflare']" />
                <i v-else class="fas fa-server" style="color: #7357D2; font-size: 1.2rem;"></i>
              </template>
            </div>
            <div class="provider-names">
              <h1 class="provider-display-name">{{ provider.display_name || provider.name }}</h1>
              <span class="provider-real-name" v-if="provider.display_name && provider.display_name !== provider.name">{{ provider.name }}</span>
            </div>
          </div>
          <div class="description-content">
            <p v-if="provider.description">{{ provider.description }}</p>
            <p v-else class="no-description">
              {{ provider.display_name || provider.name }} is a VPN infrastructure provider with {{ formatNumber(provider.stats?.total || 0) }} monitored IP addresses across multiple geographic regions. This provider offers extensive infrastructure coverage across multiple countries and regions, providing reliable VPN services to users worldwide. The network spans various data centers and hosting facilities, ensuring robust connectivity and performance for end users.
            </p>
          </div>
        </div>
      </div>

        <!-- Back Navigation -->
        <div class="navigation-footer">
          <router-link to="/providers" class="back-navigation">
            ← Back to Providers
          </router-link>
        </div>

      </div> <!-- End container provider-content -->
    </div> <!-- End main dashboard -->
  </div> <!-- End container-fluid -->
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Jura:wght@300;400;500;600;700&display=swap');

/* Main Dashboard Container - Exactly matching Providers.vue */
.provider-dashboard {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

/* Loading and Error States */
.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  text-align: center;
  color: #E5E7EB;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #374151;
  border-top: 3px solid #60A5FA;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.back-btn {
  color: #60A5FA;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.2s;
}

.back-btn:hover {
  color: #93C5FD;
}

/* Provider Header */
.provider-header {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
  gap: 1rem;
}

.provider-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.provider-thumbnail {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: cover;
}

.provider-names {
  display: flex;
  align-items: baseline;
  gap: 1rem;
  font-family: 'Jura', sans-serif;
}

.provider-display-name {
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
  color: #FFFFFF;
  line-height: 1.2;
  font-family: 'Jura', sans-serif;
}

.provider-real-name {
  font-size: 1rem;
  color: #9CA3AF;
  font-family: 'Jura', sans-serif;
}

.positive {
  color: #22c55e;
}

.negative {
  color: #ef4444;
}

/* Time Filter Buttons */
.time-filters {
  display: flex;
  gap: 0.25rem;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.25rem;
  border-radius: 0.5rem;
}

.time-btn {
  padding: 0.5rem 0.75rem;
  background: none;
  border: none;
  color: #6B7280;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
}

.time-btn.active {
  background-color: #7357D2;
  color: white;
}

.time-btn:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

/* Provider Description Section */
.description-section {
  margin-bottom: 2rem;
  margin-top: 0;
  width: 100%;
}

/* About header with icon and title */
.about-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  clear: none;
}

.description-section h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: #FFFFFF;
  clear: none;
}

.description-content p {
  color: #E5E7EB;
  line-height: 1.6;
  margin: 0;
}

.no-description {
  color: #6B7280;
  font-style: italic;
}

/* World Map Section */
.map-section {
  margin-bottom: 3rem;
  width: 100%;
  position: relative;
}

.map-container {
  width: 150%;
  margin-left: -25%;
  height: 700px;
  position: relative;
  overflow: visible;
  mask: radial-gradient(ellipse 80% 60% at center, black 40%, transparent 80%);
  -webkit-mask: radial-gradient(ellipse 80% 60% at center, black 40%, transparent 80%);
}

/* Content Section with floating layout */
.content-section {
  position: relative;
  margin-bottom: 1.5rem;
}

.content-section::after {
  content: "";
  display: table;
  clear: both;
}

.infrastructure-overview {
  float: right;
  width: calc(50% - 1rem);
  margin: 0 0 1.5rem 2rem;
}

/* Provider Overview - Boxed section */
.provider-overview {
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 1rem;
  padding: 1.5rem;
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.overview-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #FFFFFF;
}

.overview-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #FFFFFF;
}

.overview-details {
  margin-bottom: 1.5rem;
}

.overview-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.overview-label {
  color: #6B7280;
  font-size: 0.875rem;
}

.overview-value {
  font-weight: 500;
  color: #E5E7EB;
}

.overview-value.positive {
  color: #22c55e;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.action-btn.primary {
  background-color: #7357D2;
  color: white;
}

.action-btn.primary:hover {
  background-color: #7F62E3;
}

.action-btn.secondary {
  background-color: transparent;
  color: #6B7280;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.action-btn.secondary:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

/* Activity Section - Boxed */
.activity-section {
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 1rem;
  padding: 1.5rem;
}

.activity-section h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: #FFFFFF;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.activity-icon {
  font-size: 1.5rem;
}

.activity-title {
  font-weight: 500;
  color: #E5E7EB;
  margin-bottom: 0.25rem;
}

.activity-date {
  font-size: 0.875rem;
  color: #6B7280;
}

/* Provider Actions - Boxed cards */
.provider-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-card {
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.action-icon {
  font-size: 1.5rem;
}

.action-content {
  flex: 1;
}

.action-title {
  font-weight: 500;
  color: #E5E7EB;
  margin-bottom: 0.25rem;
}

.action-subtitle {
  font-size: 0.875rem;
  color: #6B7280;
}

.action-link {
  background: none;
  border: none;
  color: #7357D2;
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: underline;
}

.action-link:hover {
  color: #7F62E3;
}

/* Details Section - No box, just text content like in financial dashboard */
.details-section {
  margin-bottom: 2rem;
}

.details-section h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: #FFFFFF;
}

.details-content p {
  color: #E5E7EB;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.no-description {
  color: #6B7280;
  font-style: italic;
}

/* Stats grid - minimal styling */
.provider-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  color: #6B7280;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-value {
  font-weight: 500;
  color: #E5E7EB;
}

/* Navigation Footer - Natural background integration */
.navigation-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.back-navigation {
  color: #6B7280;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.back-navigation:hover {
  color: #E5E7EB;
  text-decoration: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .provider-dashboard {
    padding: 1rem;
  }

  .provider-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .provider-names {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .provider-display-name {
    font-size: 1.5rem;
  }

  .provider-real-name {
    font-size: 0.875rem;
  }

  .map-container {
    width: 140%;
    margin-left: -20%;
    height: 600px;
  }

  .infrastructure-overview {
    float: none;
    width: 100%;
    margin: 0 0 1.5rem 0;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>
